import React, { ReactNode, useState } from 'react';

import { Box } from '@mui/material';
import { GBPCurrency } from '@apigrowfinance/pbolt-icons';
import IllustratedMessage from '@apigrowfinance/pbolt-illustrated-message';
import { Panel, PanelContent, PanelTitle } from '@apigrowfinance/pbolt-panel';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import {
    Disbursement,
    LoanItemExpanded,
} from '@clients/domains/lending/lending';
import LoadingError from '@components/loadingError/LoadingError';
import DisbursementsPanelItem from '@components/overviewPanels/disbursementsPanel/DisbursementsPanelItem';
import DividerPanel from '@components/overviewPanels/dividerPanel/DividerPanel';
import { useAccount } from '@contexts/AccountProvider';
import { DisbursementStatus } from '@enums/lending/disbursementStatus';
import useRetrieveAccountLoanDetails from '@hooks/useRetrieveAccountLoanDetails';
import disbursements from '@messages/disbursements';
import getEnumTranslation from '@utils/getEnumTranslation';

const DisbursementsPanel = () => {
    const { account } = useAccount();
    const { data, isLoading, isError } = useRetrieveAccountLoanDetails({
        expand: true,
    });
    const loanItem = data as LoanItemExpanded;

    const navigate = useNavigate();

    const panels = loanItem?.disbursement_schedule;

    const NO_OF_ITEMS_TO_DISPLAY = 2;

    const [isHovered, setIsHovered] = useState(false);

    const viewDisbursements = () =>
        navigate(`/${account?.account_id}/disbursements`);

    const dummyPanels = [];
    if (!panels) {
        // if loading and no loanItem, then create some dummy data to be populated
        // for the skeleton
        const firstDummyData: Disbursement = {
            disbursement_id: '123',
            status: DisbursementStatus.PAID,
            due_date: '2023-06-28T00:00:00.000Z',
            credit_date: '2023-06-28T00:00:00.000Z',
            amount: 1000,
            principal: 1000,
            fee: 0,
            tax: 0,
            charge: 0,
        };
        const secondDummyData: Disbursement = {
            disbursement_id: '456',
            status: DisbursementStatus.PAID,
            due_date: '2023-06-28T00:00:00.000Z',
            credit_date: '2023-06-28T00:00:00.000Z',
            amount: 1000,
            principal: 1000,
            fee: 0,
            tax: 0,
            charge: 0,
        };
        dummyPanels?.push(firstDummyData);
        dummyPanels?.push(secondDummyData);
    }

    const firstPaidLoan = panels
        ?.map((panel: Disbursement) => panel.status)
        .lastIndexOf(DisbursementStatus.PAID);

    const relevantPanels = panels?.slice(firstPaidLoan, panels.length);

    if (isError) {
        return (
            <Panel>
                <PanelContent>
                    <LoadingError />
                </PanelContent>
            </Panel>
        );
    }

    return (
        <Panel
            onClick={isLoading ? null : viewDisbursements}
            isHovered={setIsHovered}>
            <PanelTitle
                hovered={isHovered}
                loading={isLoading}
                arrowLabel={
                    <FormattedMessage
                        id="DisbursementsPanel.viewDisbursements"
                        defaultMessage="View disbursements"
                    />
                }>
                <FormattedMessage
                    id="DisbursementsPanel.disbursements"
                    defaultMessage="Disbursements"
                />
            </PanelTitle>
            <PanelContent>
                <DividerPanel isLoading={isLoading} isClickable={false}>
                    {isLoading && (
                        <>
                            {dummyPanels?.map((panel, i) => {
                                return (
                                    <DisbursementsPanelItem
                                        key={i}
                                        itemData={panel}
                                        isLoading={isLoading}
                                        dateCaption="12-12-2022"
                                    />
                                );
                            })}
                        </>
                    )}

                    {!isLoading && !relevantPanels && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexDirection: 'column',
                                flex: '1 1 0',
                                textAlign: 'center',
                            }}>
                            <IllustratedMessage
                                icon={<GBPCurrency color="inherit" />}
                                messageMain={
                                    <FormattedMessage
                                        id="DisbursementsPanel.noDisbursements"
                                        defaultMessage="No disbursements"
                                    />
                                }
                                messageSub={
                                    <FormattedMessage
                                        id="DisbursementsPanel.noDisbursementsFound"
                                        defaultMessage="No disbursements were found for this account"
                                    />
                                }
                            />
                        </Box>
                    )}

                    {!isLoading &&
                        relevantPanels?.length &&
                        relevantPanels
                            ?.map((panel: Disbursement, i) => {
                                let dateCaption: ReactNode;
                                if (panels.length === 1) {
                                    dateCaption = getEnumTranslation(
                                        'DISBURSEMENT_DATE',
                                        {
                                            messages: disbursements,
                                        },
                                    );
                                } else {
                                    i === 0
                                        ? (dateCaption = getEnumTranslation(
                                              'FIRST',
                                              {
                                                  messages: disbursements,
                                              },
                                          ))
                                        : (dateCaption = getEnumTranslation(
                                              'NEXT',
                                              {
                                                  messages: disbursements,
                                              },
                                          ));
                                }
                                return (
                                    <DisbursementsPanelItem
                                        key={panel?.disbursement_id}
                                        itemData={panel}
                                        isLoading={isLoading}
                                        dateCaption={dateCaption}
                                    />
                                );
                            })
                            .filter((panel, i) => i < NO_OF_ITEMS_TO_DISPLAY)}
                </DividerPanel>
            </PanelContent>
        </Panel>
    );
};

export default DisbursementsPanel;
